import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import PropTypes from "prop-types";
import { useAuth, useLinks } from "hooks";
import { APPLICANT_TYPES, MISSION_ROLE_STATES } from "constants/index";
// import { ApplicationDetailsModal } from "components/Missions/Applicants"; This is failing in default props
import ApplicationDetailsModal from "./ApplicationDetailsModal";
import { DataTableProvider } from "components/DataTable";

let timer;

/**
 * ApplicantList
 *
 * @param {Object}    data
 * @param {Boolean}   showAdvancedOptions
 * @param {Function}  getAdvancedOptions
 * @param {Number}    resultsPerPage
 * @param {Object}    selectedState
 * @param {Array}     columns
 * @param {Function}  handleNextPage
 * @param {Function}  handlePreviousPage
 * @param {Boolean}   hasNextPage
 * @param {Boolean}   hasPreviousPage
 * @param {Number}    currentPage
 * @param {Object}    pageInfo
 * @param {Boolean}   loading
 * @param {Function}  handlePageChange
 * @param {Number}    totalCount
 * @param {Element}   applicationModalComponent
 * @param {Element}   paginationRightContainer
 * @param {Boolean}   modalConfigFunc
 */
const ApplicantList = ({
  data: dataProp,
  showAdvancedOptions,
  getAdvancedOptions,
  resultsPerPage,
  selectedState,
  requestedState,
  columns,
  handleNextPage,
  handlePreviousPage,
  hasNextPage,
  hasPreviousPage,
  currentPage,
  pageInfo,
  loading: loadingProp,
  handlePageChange,
  totalCount,
  applicationModalComponent: ApplicationModalComponent,
  paginationRightContainer: PaginationRightContainer,
  modalConfigFunc,
  ...props
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const applicant = new URLSearchParams(search).get("application");
  const { roles, isShowcase, isMember, isAdmin } = useAuth();
  const links = useLinks();
  const [application, setApplication] = useState();
  const [selected, setSelected] = useState([]);
  const [selectable, setSelectable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const navigateToApplication = (application) => navigate(links.mission_application(application?.uuid));

  const handleSelect = useCallback((items) => {
    const unique = [...new Set(items)];
    setSelected((prev) => {
      const isSame = prev.length === unique.length && prev.every((v, i) => v === unique[i]);
      return isSame ? prev : unique;
    });
  }, []);

  /**
   * @description Set application state when applicant is provided.
   * This is used to open the application modal when an applicant is selected.
   */
  useEffect(() => {
    if (applicant) {
      setApplication({ id: applicant });
    }
  }, [applicant]);

  /**
   * @description Sync `loading` with `loadingProp` or `applicantState` state changes.
   */
  useEffect(() => {
    timer = setTimeout(() => setLoading(loadingProp), loadingProp ? 500 : 0);

    return () => clearTimeout(timer);
  }, [loadingProp, selectedState?.applicantState]);

  /**
   * @description Sync `data` with `dataProp` or `loadingProp` state changes.
   */
  useEffect(() => {
    if (loadingProp) return;

    const matchesRequestedState = requestedState === selectedState?.applicantState;

    if (!requestedState || matchesRequestedState) {
      setData(dataProp);
    }
  }, [loadingProp, dataProp, requestedState, selectedState]);

  /**
   * @description Set selectable based on state and advanced options.
   */
  useEffect(() => {
    setSelectable(showAdvancedOptions && selectedState?.applicantState !== APPLICANT_TYPES.invited);
  }, [showAdvancedOptions, selectedState?.applicantState]);

  /**
   * @description Memoizes the filtered columns after building the context.
   */
  const filteredColumns = useMemo(() => {
    const context = {
      setApplication,
      navigateToApplication,
      modalConfigFunc,
      isShowcase,
      isMember,
      isAdmin,
      selectedState,
    };
    const buildColumnsContext = (cols, context) =>
      cols?.filter(Boolean)?.map((col) => (typeof col === "function" ? col(context) : col));
    const contextColumns = buildColumnsContext(columns, context);
    const roleNames = roles?.map((role) => role.name);

    const filterColumns = (cols, currentState) =>
      cols.filter(
        ({ restrict }) =>
          !restrict ||
          (restrict.roles?.some((allowed) => roleNames.includes(allowed)) && restrict?.states?.includes(currentState))
      );

    return filterColumns(contextColumns, selectedState?.applicantState);
  }, [columns, roles, selectedState, setApplication]);

  const isRoleClosed = ({ state }) => state === MISSION_ROLE_STATES.closed;

  return (
    <>
      <DataTableProvider
        data={data}
        selected={selected}
        selectable={selectable}
        onSelect={handleSelect}
        columns={filteredColumns}
        selectionInitialState={selected}
        resultsPerPage={resultsPerPage}
        clearable={false}
        clearCacheValue={selectedState?.applicantState}
        loading={loadingProp || loading}
        rowOptions={
          selectable ? ({ id, ...rest }) => getAdvancedOptions({ id: id || rest.profile?.uuid, ...rest }) : null
        }
        headerOptions={
          selectable
            ? (selection) => getAdvancedOptions({ missionRole: { slug: data?.[0]?.missionRole?.slug } }, selection)
            : null
        }
        selectionKeyFunc={({ id, profile }) =>
          APPLICANT_TYPES.recommended === selectedState?.applicantState ? profile?.uuid : id
        }
        selectFieldDisabledFunc={({ missionRole }) => isRoleClosed(missionRole)}
        rowOptionsTooltipIdFunc={({ missionRole }) =>
          isRoleClosed(missionRole) && "Missions.Applicants.ApplicantAdvancedOptions.RoleClosedTooltip"
        }
        headerOptionsTooltipIdFunc={({ data, selectableDataIds }) =>
          data?.length > 0 &&
          selectableDataIds?.length === 0 &&
          "Missions.Applicants.ApplicantAdvancedOptions.RoleClosedTooltip"
        }
        paginationProps={{
          countMessage: getCountMsgId(selectedState?.applicantState),
          onNextPage: handleNextPage,
          onPreviousPage: handlePreviousPage,
          hasNextPage: !loading && hasNextPage,
          hasPreviousPage: !loading && hasPreviousPage,
          resultsPerPage,
          currentPage,
          pageInfo,
          loading: loading,
          handlePageChange,
          totalCount,
          rightContainer: PaginationRightContainer ? (
            <PaginationRightContainer selected={selected} applicantState={selectedState?.applicantState} />
          ) : null,
        }}
        {...props}
      />
      {application && (
        <ApplicationModalComponent
          application={application}
          show={!!application}
          setApplication={setApplication}
          {...modalConfigFunc?.(application)}
        />
      )}
    </>
  );
};

const getCountMsgId = (state) => {
  switch (state) {
    case APPLICANT_TYPES.pending:
    case APPLICANT_TYPES.applied:
    case APPLICANT_TYPES.shortlisted:
    case APPLICANT_TYPES.interviewed:
    case APPLICANT_TYPES.selected:
    case APPLICANT_TYPES.unselected:
      return "Global.Applications.Count";
    case APPLICANT_TYPES.recommended:
      return "Global.Recommendations.Count";
    default:
      return "Global.Invitations.Count";
  }
};

ApplicantList.defaultProps = {
  showAdvancedOptions: false,
  columns: [],
  applicationModalComponent: ApplicationDetailsModal,
};

ApplicantList.propTypes = {
  data: PropTypes.array,
  showAdvancedOptions: PropTypes.bool,
  getAdvancedOptions: PropTypes.func,
  resultsPerPage: PropTypes.number,
  selectedState: PropTypes.object,
  requestedState: PropTypes.string,
  columns: PropTypes.array,
  handleNextPage: PropTypes.func,
  handlePreviousPage: PropTypes.func,
  hasNextPage: PropTypes.bool,
  hasPreviousPage: PropTypes.bool,
  currentPage: PropTypes.number,
  pageInfo: PropTypes.object,
  loading: PropTypes.bool,
  handlePageChange: PropTypes.func,
  totalCount: PropTypes.number,
  applicationModalComponent: PropTypes.element,
  paginationRightContainer: PropTypes.element,
  modalConfigFunc: PropTypes.func,
};

export default ApplicantList;
